import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TextTruncate from 'react-text-truncate';
import { exchangeMeal, scrollToNextMeal } from '../helpers/mealExchangeHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch } from 'react-redux';
import {
  mealExchangeChangeSelectedMeal,
  mealExchangeChangeSelectedMealModalStatus,
} from '../../../../redux/actions/orderFormActions';
import { getMealExchangeActiveDay } from '../../../../redux/selectors/orderFormSelector';
import { InfoIcon } from '../../../../assets/icons/infoIcon';
import { getServerConfig } from '../../../../features/general/generalSlice';
import { getVisibleNutrition } from '../../../../redux/selectors/formSettingsSelector';
import { useAppSelector } from '../../../../config/hooks';
import CustomLightbox from '../../../common/CustomLightbox';
import { classNamesUtil } from '../../../../utils/classNamesUtil.util';

type Props = {
  coursePhotoVisibleInForm: boolean;
  id: string;
  // TODO ANY
  meal: any;
  mealId: number;
  refs: Array<React.Ref<unknown>>;
};

const Meal = ({ coursePhotoVisibleInForm, id, meal, mealId, refs }: Props) => {
  const dispatch = useDispatch();
  const activeDay = useAppSelector(getMealExchangeActiveDay);
  const { apiKey, companyId, serverUrl } = useAppSelector(getServerConfig);
  const showNutrition = useAppSelector(getVisibleNutrition);

  const [index, setIndex] = useState(-1);

  const {
    calories,
    courseClientImageUrl,
    dietOptionName,
    ingredientCalories,
    ingredientCarbohydrate,
    ingredientFat,
    ingredientProtein,
    mealName,
    menuAvailable,
    menuMealName,
    reviewsNumber,
    reviewsScore,
    selected,
  } = meal;

  const colorReview = (
    reviewsScore: number,
    reviewsNumber: number,
    red: string,
    yellow: string,
    green: string
  ) => {
    if (reviewsNumber === 0) {
      return green;
    }
    if (reviewsScore >= 66) {
      return green;
    }
    if (reviewsScore >= 33 && reviewsScore < 66) {
      return yellow;
    }
    return red;
  };

  const handleExchangeMealClick = () => {
    if (!selected && index === -1) {
      dispatch(
        // @ts-ignore TODO
        exchangeMeal(companyId, activeDay, id, meal, mealId, serverUrl, apiKey)
      );
      scrollToNextMeal(refs, id);
    }
  };

  return (
    <div
      className="meal"
      onClick={handleExchangeMealClick}
      onKeyDown={handleExchangeMealClick}
      role="button"
      tabIndex={0}
    >
      {selected && (
        <div className="meal__check">
          <FontAwesomeIcon className="font-size-15" icon={faCheck} />
        </div>
      )}

      <div
        className={classNamesUtil('meal__container', {
          'meal__container--active': selected,
        })}
      >
        <div className="display-flex">
          {coursePhotoVisibleInForm && (
            <>
              <img
                alt=""
                className="meal__image"
                height={80}
                onClick={e => {
                  if (courseClientImageUrl) {
                    e.stopPropagation();
                    setIndex(0);
                  }
                }}
                src={
                  courseClientImageUrl ||
                  'https://static.dietly.pl/form/placeholder.jpg'
                }
                width={80}
              />

              {courseClientImageUrl && (
                <CustomLightbox
                  closeOnClick
                  index={index}
                  isOpen={index !== -1}
                  onClose={() => setIndex(-1)}
                  singleImage
                  slides={[{ src: courseClientImageUrl }]}
                />
              )}
            </>
          )}

          <div>
            <div
              className={classNamesUtil('', {
                'display-flex spacer-bottom-4': !coursePhotoVisibleInForm,
              })}
            >
              <div className="meal__label-wrapper">
                <div className="meal__label meal__label--gray">
                  {dietOptionName}
                </div>
              </div>

              {menuAvailable && (
                <div
                  className={classNamesUtil('display-flex align-items-center', {
                    'spacer-vertical-4': coursePhotoVisibleInForm,
                  })}
                >
                  {!!reviewsNumber && (
                    <div
                      className={`meal__label ${colorReview(
                        reviewsScore || 0,
                        reviewsNumber,
                        'meal__label--red',
                        'meal__label--yellow',
                        'meal__label--green'
                      )}`}
                    >
                      {reviewsScore}%
                    </div>
                  )}

                  <div className="label-s color-gray-900">
                    {reviewsNumber} opinii
                  </div>
                </div>
              )}
            </div>

            <div className="meal__title">
              {menuAvailable ? (
                <TextTruncate
                  element="h5"
                  line={2}
                  text={menuMealName}
                  truncateText="…"
                />
              ) : (
                <h5>
                  {mealName} z diety {calories} kcal
                </h5>
              )}
            </div>
          </div>
        </div>

        {!menuAvailable ? (
          <p className="body-m spacer-top-8">
            Danie nie zostało jeszcze ustalone przez catering
          </p>
        ) : (
          showNutrition && (
            <div className="meal__info-wrapper">
              <InfoIcon color="#62748C" height="24" width="24" />

              <div className="color-gray-400">
                {ingredientCalories && ingredientCalories > 0 && (
                  <span>{Math.floor(ingredientCalories)}kcal • </span>
                )}

                {ingredientProtein && ingredientProtein > 0 && (
                  <span>B: {Math.floor(ingredientProtein)}g • </span>
                )}

                {ingredientCarbohydrate && ingredientCarbohydrate > 0 && (
                  <span>W: {Math.floor(ingredientCarbohydrate)}g • </span>
                )}

                {ingredientFat && ingredientFat > 0 && (
                  <span>T: {Math.floor(ingredientFat)}g</span>
                )}
              </div>
            </div>
          )
        )}

        {menuAvailable && showNutrition && (
          <div
            className="label-s color-primary-500 spacer-top-8"
            onClick={e => {
              e.stopPropagation();
              dispatch(mealExchangeChangeSelectedMeal(meal));
              dispatch(mealExchangeChangeSelectedMealModalStatus(true));
            }}
            onKeyDown={e => {
              e.stopPropagation();
              dispatch(mealExchangeChangeSelectedMeal(meal));
              dispatch(mealExchangeChangeSelectedMealModalStatus(true));
            }}
            role="button"
            tabIndex={0}
          >
            Zobacz szczegóły
          </div>
        )}
      </div>
    </div>
  );
};

Meal.propTypes = {
  id: PropTypes.string.isRequired,
  meal: PropTypes.object.isRequired,
  mealId: PropTypes.number.isRequired,
  refs: PropTypes.object,
};

export default Meal;
